import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import paymentGatewayService from '../../services/paymentGatewayService';
import GatewayDocuments from '../../components/GatewayDocuments';
import Modal from '../../components/Modal';
import { useAppDialog } from '../../components/AppDialog';
import Pagination from '../../components/Pagination';
import MultiReferenceSelect from '../../components/MultiReferenceSelect';
import RecordReferenceSelect from '../../components/tickets/RecordReferenceSelect';
import { buildPaymentGatewayPayload, buildGatewayRateRules, costingRateKey, gatewayRateDirections, gatewayRateInputs, formatGatewayRate } from '../../utils/paymentGatewayPayload';
import { SearchIcon, EditIcon, TrashIcon } from '../../components/Icons';
import './PaymentGatewayRecords.css';

const resources = {
  categories: { label: 'Payment Gateway Category', plural: 'Payment Gateway Categories' },
  sources: { label: 'Payment Gateway Provider Source', plural: 'Payment Gateway Provider Sources' },
  providers: { label: 'Payment Gateway Provider', plural: 'Payment Gateway Providers' },
  'master-providers': { label: 'L1 Payment Gateway Provider', plural: 'L1 Payment Gateway Providers' }
};
const providerReferences = [
  { key: 'category', label: 'Payment Gateway Categories', itemName: 'categories' },
  { key: 'country', label: 'Countries', itemName: 'countries' },
  { key: 'currency', label: 'Currencies', itemName: 'currencies' }
];
const emptyReferences = () => Object.fromEntries(providerReferences.map(field => [field.key, []]));
const newForm = () => ({ code: '', name: '', provider_id: '', source: '', costing_rate_rules: [], description: '', is_active: true, ...emptyReferences(), group_links: [], documents: [] });
const referenceList = value => Array.isArray(value) ? value : value ? [value] : [];
const referenceLabel = value => referenceList(value).map(item => `${item.currency_code || item.code} — ${item.name}`).join(', ') || '—';
const rateReference = (rule, key, options) => referenceList(options[key]).find(option => String(option._id) === String(rule[key]?._id || rule[key]));
const rateLabel = (rule, key, options) => {
  const item = rateReference(rule, key, options);
  return item?.currency_code || item?.code || '—';
};
const rateCombinationLabel = (rule, options) => providerReferences.map(field => rateLabel(rule, field.key, options)).join(' / ');
const errorMessage = (error) => error?.response?.data?.message || error.message || 'Unable to complete the request.';

const ProviderCoverage = ({ record, showRates }) => {
  const [expanded, setExpanded] = useState(false);
  const rules = buildGatewayRateRules(record, record.costing_rate_rules, record.costing_rates ?? 0);
  if (!rules.length) return <span className="payment-gateway-hint">No combinations configured.</span>;
  return (
    <div className="payment-gateway-coverage">
      <table className="table-striped payment-gateway-coverage-table" aria-label={`${record.code} combinations${showRates ? ' and costing rates' : ''}`}>
        <thead><tr><th scope="col">Category</th><th scope="col">Country</th><th scope="col">Currency</th>{showRates && <><th scope="col">In Costing</th><th scope="col">Out Costing</th></>}</tr></thead>
        <tbody>{(expanded ? rules : rules.slice(0, 3)).map(rule => <tr key={costingRateKey(rule)}>
          {providerReferences.map(field => <td key={field.key}>
            <span title={referenceLabel(rateReference(rule, field.key, record))}>{rateLabel(rule, field.key, record)}</span>
          </td>)}
          {showRates && gatewayRateDirections.map(direction => <td key={direction.key} className="payment-gateway-coverage-rate">{formatGatewayRate(rule, direction, rateLabel(rule, 'currency', record))}</td>)}
        </tr>)}</tbody>
      </table>
      {rules.length > 3 && <button type="button" className="payment-gateway-coverage-toggle" aria-expanded={expanded}
        aria-label={`${expanded ? 'Show fewer' : 'Show all'} combinations for ${record.code}`} onClick={() => setExpanded(value => !value)}>
        {expanded ? 'Show less' : `Show all ${rules.length} combinations`}
      </button>}
    </div>
  );
};

const PaymentGatewayRecords = ({ resource }) => {
  const { hasPermission } = useAuth();
  const { confirm } = useAppDialog();
  const config = resources[resource];
  const permission = `payment_gateway_${resource.replace(/-/g, '_')}`;
  const canView = hasPermission(permission, 'view');
  const canCreate = hasPermission(permission, 'create');
  const canEdit = hasPermission(permission, 'edit');
  const canDelete = hasPermission(permission, 'delete');
  const showActions = canEdit || canDelete;
  const isMaster = resource === 'master-providers';
  const canViewFinancial = isMaster && hasPermission(permission, 'view_financial');
  const canEditFinancial = isMaster && hasPermission(permission, 'edit_financial');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [refresh, setRefresh] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(newForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [referenceOptions, setReferenceOptions] = useState(emptyReferences);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [optionsAttempt, setOptionsAttempt] = useState(0);

  useEffect(() => {
    if (!canView) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const result = await paymentGatewayService.list(resource, { page, limit, search, is_active: status });
        if (!active) return;
        if (!result.success) throw new Error(result.message);
        if (page > 1 && page > Math.max(1, result.pagination.pages)) {
          setPage(Math.max(1, result.pagination.pages));
          return;
        }
        setRecords(result.data);
        setPagination(result.pagination);
      } catch (err) {
        if (active) setError(errorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [resource, canView, page, limit, search, status, refresh]);

  useEffect(() => {
    if (!isOpen || !isMaster) return undefined;
    let active = true;
    setOptionsLoading(true);
    setOptionsError('');
    paymentGatewayService.getMasterProviderOptions().then(result => {
      if (!result.success) throw new Error(result.message);
      if (active) setReferenceOptions(result.data);
    }).catch(err => {
      if (active) setOptionsError(errorMessage(err));
    }).finally(() => {
      if (active) setOptionsLoading(false);
    });
    return () => { active = false; };
  }, [isOpen, isMaster, optionsAttempt]);

  const openForm = (record = null) => {
    setEditing(record);
    setForm(record ? {
      ...newForm(), code: record.code, name: record.name, description: record.description || '',
      is_active: record.is_active,
      costing_rate_rules: buildGatewayRateRules(record, record.costing_rate_rules, record.costing_rates ?? 0),
      provider_id: record.provider_id?._id || '', source: record.source?._id || '',
      ...Object.fromEntries(providerReferences.map(field => [field.key, referenceList(record[field.key]).map(item => item._id || item)])),
      group_links: (record.group_links || []).map(link => ({ ...link })),
      documents: (record.documents || []).map(document => ({ ...document }))
    } : newForm());
    setFormError('');
    setIsOpen(true);
  };
  const closeForm = () => { if (!saving) setIsOpen(false); };
  const updateField = (field, value) => setForm(prev => {
    const next = { ...prev, [field]: value };
    if (isMaster && providerReferences.some(item => item.key === field)) next.costing_rate_rules = buildGatewayRateRules(next, prev.costing_rate_rules);
    return next;
  });
  const updateLink = (index, field, value) => setForm(prev => ({
    ...prev, group_links: prev.group_links.map((link, i) => i === index ? { ...link, [field]: value } : link)
  }));

  const saveRecord = async (event) => {
    event.preventDefault();
    const missing = isMaster && providerReferences.find(field => !referenceList(form[field.key]).length);
    if (missing) { setFormError(`Select at least one option for ${missing.label.toLowerCase()}.`); return; }
    setSaving(true);
    setFormError('');
    const data = buildPaymentGatewayPayload(form, { isMaster, canViewFinancial, canEditFinancial });
    try {
      const result = editing
        ? await paymentGatewayService.update(resource, editing._id, data)
        : await paymentGatewayService.create(resource, data);
      if (!result.success) throw new Error(result.message);
      setIsOpen(false);
      setRefresh(value => value + 1);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (record) => {
    if (!await confirm({ title: `Delete ${config.label}`, message: `Delete ${record.name || record.code}? This action cannot be undone.`, confirmLabel: 'Delete', variant: 'danger' })) return;
    setDeleting(true);
    setError('');
    try {
      const result = await paymentGatewayService.remove(resource, record._id);
      if (!result.success) throw new Error(result.message);
      setRefresh(value => value + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  if (!canView) return <div className="card" style={{ padding: '2rem' }}>You do not have permission to view {config.plural.toLowerCase()}.</div>;

  const optionsFor = (field) => {
    const options = referenceOptions[field.key] || [];
    return [...options, ...referenceList(editing?.[field.key]).filter(item => item._id && !options.some(option => option._id === item._id))];
  };
  const rateOptions = Object.fromEntries(providerReferences.map(field => [field.key, optionsFor(field)]));
  const missingReferences = isMaster && (!form.provider_id || !form.source || providerReferences.some(field => !referenceList(form[field.key]).length));
  const columnCount = 4 + (showActions ? 1 : 0);
  const descriptionField = <label>Description<textarea className="input" rows={3} value={form.description} onChange={event => updateField('description', event.target.value)} disabled={saving} /></label>;
  const activeField = <div className="payment-gateway-active">
    <span>{isMaster ? 'Active' : 'Is Active'}</span>
    <label className="switch">
      <input type="checkbox" aria-label="Is Active" checked={form.is_active} onChange={event => updateField('is_active', event.target.checked)} disabled={saving} />
      <span className="slider"></span>
    </label>
  </div>;

  return (
    <div className={`payment-gateway-records${isMaster ? ' payment-gateway-master-records' : ''}`}>
      {canCreate && !isMaster && <div className="payment-gateway-toolbar"><button className="btn btn-primary" onClick={() => openForm()}>Add {config.label}</button></div>}
      <div className="card payment-gateway-filters">
        <div className="search-container">
          <div className="search-icon-wrapper"><SearchIcon /></div>
          <input className="search-input" aria-label={`Search ${config.plural.toLowerCase()}`} placeholder="Search code, name, or description..." value={search}
            onChange={event => { setSearch(event.target.value); setPage(1); }} />
        </div>
        <select className="input" aria-label="Filter by status" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}>
          <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
        </select>
        {isMaster && canCreate && <button className="btn btn-primary payment-gateway-create" onClick={() => openForm()}>Add Provider</button>}
      </div>
      {error && <div className="card payment-gateway-error" role="alert">{error} <button className="btn btn-secondary" onClick={() => setRefresh(value => value + 1)}>Retry</button></div>}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className={`table-striped payment-gateway-table${isMaster ? ' payment-gateway-provider-table' : ''}`} aria-label={config.plural} aria-busy={loading}>
            <thead><tr>
              {isMaster ? <><th className="payment-gateway-identity-column">Provider</th><th>{canViewFinancial ? 'Coverage & Costing Rates' : 'Coverage'}</th></> : <><th>Code</th><th>Name</th><th>Description</th></>}
              {isMaster && <th className="payment-gateway-links-column">Group Links</th>}<th className={isMaster ? 'payment-gateway-status-column' : undefined}>Status</th>{showActions && <th className="payment-gateway-actions">Actions</th>}
            </tr></thead>
            <tbody style={{ opacity: loading ? 0.5 : 1 }}>
              {records.length === 0 ? <tr><td colSpan={columnCount} className="payment-gateway-empty">{loading ? 'Loading...' : `No ${config.plural.toLowerCase()} found.`}</td></tr> : records.map(record => (
                <tr key={record._id}>
                  {isMaster ? <>
                    <td><div className="payment-gateway-identity">
                      <strong className="payment-gateway-provider-name">{record.provider_id?.name || record.provider_id?.code || '—'}</strong>
                      <code className="payment-gateway-provider-code">{record.code}</code>
                      {!!record.documents?.length && <span className="payment-gateway-provider-source">{record.documents.length} document(s)</span>}
                      <span className="payment-gateway-provider-source">Source · {record.source?.name || record.source?.code || '—'}{record.source?.name && record.source?.code ? ` (${record.source.code})` : ''}</span>
                      {record.description && <p className="payment-gateway-provider-description" title={record.description}>{record.description}</p>}
                    </div></td>
                    <td><ProviderCoverage record={record} showRates={canViewFinancial} /></td>
                  </> : <><td>{record.code}</td><td>{record.name}</td><td>{record.description || '—'}</td></>}
                  {isMaster && <td>
                    {(record.group_links || []).length ? record.group_links.map((link, index) => <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{link.name || `Link ${index + 1}`}</a>) : '—'}
                  </td>}
                  <td><span className={`payment-gateway-status ${record.is_active ? 'active' : 'inactive'}`}>{record.is_active ? 'Active' : 'Inactive'}</span></td>
                  {showActions && <td className="payment-gateway-actions"><div className="payment-gateway-row-actions">
                    {canEdit && <button className="btn" onClick={() => openForm(record)} disabled={deleting}><EditIcon size={14} /> Edit</button>}
                    {canDelete && <button className="btn payment-gateway-delete" onClick={() => deleteRecord(record)} disabled={deleting}
                      aria-label={isMaster ? `Delete ${record.code}` : undefined} title={isMaster ? `Delete ${record.code}` : undefined}><TrashIcon size={14} />{!isMaster && ' Delete'}</button>}
                  </div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={setPage} onLimitChange={value => { setLimit(value); setPage(1); }} loading={loading || deleting} itemName={isMaster ? 'providers' : config.plural.toLowerCase()} />
      </div>
      <Modal isOpen={isOpen} onClose={closeForm} title={isMaster ? `${editing ? 'Edit' : 'Add'} Payment Gateway` : `${editing ? 'Edit' : 'Add New'} ${config.label}`} maxWidth={isMaster ? '960px' : undefined}>
        <form onSubmit={saveRecord} className={`payment-gateway-form${isMaster ? ' payment-gateway-master-form' : ''}`}>
          {formError && <div role="alert" className="payment-gateway-error">{formError}</div>}
          {!isMaster && <>
            <label>Code *<input className="input" required value={form.code} onChange={event => updateField('code', event.target.value)} disabled={saving} /></label>
            <label>Name *<input className="input" required value={form.name} onChange={event => updateField('name', event.target.value)} disabled={saving} /></label>
          </>}
          {isMaster && <>
            <div className="payment-gateway-identity-grid">
              <RecordReferenceSelect field={{ key: 'provider_id', label: 'Provider', required: true, showCategory: false, options: optionsFor({ key: 'provider_id' }) }}
                value={form.provider_id} onChange={value => updateField('provider_id', value)} disabled={saving || optionsLoading || !!optionsError} />
              <RecordReferenceSelect field={{ key: 'source', label: 'Source', required: true, options: optionsFor({ key: 'source' }) }}
                value={form.source} onChange={value => updateField('source', value)} disabled={saving || optionsLoading || !!optionsError} />
            </div>
          </>}
          {isMaster && <>
            <div className="payment-gateway-reference-grid">
              {providerReferences.map(field => <MultiReferenceSelect key={field.key} label={field.key === 'category' ? 'Categories' : field.label} itemName={field.itemName} required compact closeOnAdd value={form[field.key]} options={optionsFor(field)}
                onChange={value => updateField(field.key, value)} disabled={saving || optionsLoading || !!optionsError} />)}
            </div>
            {optionsLoading && <p className="payment-gateway-hint" role="status">Loading categories, countries, and currencies...</p>}
            {optionsError ? <div role="alert" className="payment-gateway-error">{optionsError} <button type="button" className="btn btn-secondary" onClick={() => setOptionsAttempt(value => value + 1)}>Retry</button></div>
              : !optionsLoading && providerReferences.some(field => !optionsFor(field).length) && <p className="payment-gateway-hint">Create active records for any empty selection before adding a provider.</p>}
          </>}
          {canViewFinancial && <section className="payment-gateway-rates" aria-label="Costing rates by combination">
            <h4>Costing Rates</h4>
            {form.costing_rate_rules.length ? <div className="payment-gateway-rates-scroll">
              <table className="table-striped payment-gateway-rates-table">
                <thead><tr><th>Category</th><th>Country</th><th>Currency</th>{gatewayRateInputs.map(field => <th key={field.key}>{field.label}</th>)}</tr></thead>
                <tbody>{form.costing_rate_rules.map((rule, index) => <tr key={costingRateKey(rule)}>
                  {providerReferences.map(field => <td key={field.key}>{rateLabel(rule, field.key, rateOptions)}</td>)}
                  {gatewayRateInputs.map(field => <td key={field.key}>
                    <input className="input" type="number" min="0" max={field.max} step="any" required value={rule[field.key]}
                      aria-label={`${field.label} costing rate for ${rateCombinationLabel(rule, rateOptions)}`}
                      onChange={event => updateField('costing_rate_rules', form.costing_rate_rules.map((item, i) => i === index ? { ...item, [field.key]: event.target.value } : item))}
                      disabled={saving || !canEditFinancial} />
                  </td>)}
                </tr>)}</tbody>
              </table>
            </div> : <p className="payment-gateway-hint">Select categories, countries and currencies to configure rates.</p>}
          </section>}
          {descriptionField}
          {isMaster && <GatewayDocuments documents={form.documents} canEdit={!saving}
            saveHint="Uploads when you save this L1 payment gateway provider." onChange={documents => updateField('documents', documents)} />}
          {isMaster && <>
            <div className="payment-gateway-form">
              <span>Group Links</span>
              {form.group_links.map((link, index) => <div className="payment-gateway-link" key={index}>
                <input className="input" aria-label={`Group link ${index + 1} name`} placeholder="Name" value={link.name} onChange={event => updateLink(index, 'name', event.target.value)} disabled={saving} />
                <input className="input" type="url" required aria-label={`Group link ${index + 1} URL`} placeholder="https://..." value={link.url} onChange={event => updateLink(index, 'url', event.target.value)} disabled={saving} />
                <button type="button" className="btn btn-secondary" onClick={() => updateField('group_links', form.group_links.filter((_, i) => i !== index))} disabled={saving}>Remove</button>
              </div>)}
              <button type="button" className="btn btn-secondary payment-gateway-add-link" onClick={() => updateField('group_links', [...form.group_links, { name: '', url: '' }])} disabled={saving}>+ Add Group Link</button>
            </div>
          </>}
          {!isMaster && activeField}
          <div className="payment-gateway-form-actions">
            {isMaster && activeField}
            <button type="button" className="btn" onClick={closeForm} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || (isMaster && (optionsLoading || !!optionsError || missingReferences))}>{saving ? 'Saving...' : isMaster ? (editing ? 'Save Changes' : 'Create Provider') : `${editing ? 'Update' : 'Create'} ${config.label}`}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PaymentGatewayRecords;
